import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

/* ============================================================
   TYPES
============================================================ */

export type TrabajadorAsignable = {
  id_usuario: string;
  nombre_completo: string;
  email: string;
  foto: string | null;
  asignado: boolean;
};

/* ============================================================
   HOOK
============================================================ */

export function useAsignarTrabajadoresServicio(
  idEmpresa: string,
  idServicio: string | null
) {
  const [trabajadores, setTrabajadores] = useState<TrabajadorAsignable[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ======================================================
     FETCH — trabajadores de la empresa + asignaciones actuales
  ====================================================== */

  const fetchTrabajadores = useCallback(async () => {
    if (!idEmpresa || !idServicio) return;
    setLoading(true);

    try {
      const { data: rawTrabajadores, error: errorTrabajadores } = await supabase
        .from('vista_trabajadores_detalles')
        .select('id_usuario, nombre_completo, email, foto')
        .eq('id_empresa', idEmpresa)
        .eq('activo', true);

      if (errorTrabajadores) throw errorTrabajadores;

      const { data: asignados, error: errorAsignados } = await supabase
        .from('servicios_usuarios')
        .select('id_usuario, activo')
        .eq('id_servicio', idServicio);

      if (errorAsignados) throw errorAsignados;

      const mapAsignados = new Map(
        (asignados || []).map((a) => [String(a.id_usuario), a.activo])
      );

      setTrabajadores(
        (rawTrabajadores || []).map((t) => ({
          id_usuario: t.id_usuario,
          nombre_completo: t.nombre_completo || '',
          email: t.email || '',
          foto: t.foto ?? null,
          asignado: mapAsignados.get(String(t.id_usuario)) ?? false,
        }))
      );
    } catch (err) {
      Sentry.captureException(err, { extra: { idEmpresa, idServicio } });
      toast.error('Error cargando los trabajadores');
    } finally {
      setLoading(false);
    }
  }, [idEmpresa, idServicio]);

  /* ======================================================
     TOGGLE LOCAL
  ====================================================== */

  const toggleTrabajador = (id_usuario: string, asignado: boolean) => {
    setTrabajadores((prev) =>
      prev.map((t) => (t.id_usuario === id_usuario ? { ...t, asignado } : t))
    );
  };

  /* ======================================================
     SAVE — delete all + insert new
  ====================================================== */

  const guardarAsignacion = async () => {
    if (!idServicio) return { ok: false };
    const idUsuarioSistema = localStorage.getItem('id_usuario') || '';

    setSaving(true);

    try {
      const { error: deleteError } = await supabase
        .from('servicios_usuarios')
        .delete()
        .eq('id_servicio', idServicio);

      if (deleteError) throw deleteError;

      const payload = trabajadores.map((t) => ({
        id_usuario: t.id_usuario,
        id_servicio: idServicio,
        activo: t.asignado,
        usuario_creacion: idUsuarioSistema,
      }));

      if (payload.length > 0) {
        const { error: insertError } = await supabase
          .from('servicios_usuarios')
          .insert(payload);

        if (insertError) throw insertError;
      }

      toast.success('Trabajadores asignados correctamente');
      return { ok: true };
    } catch (err) {
      Sentry.captureException(err, { extra: { idServicio } });
      toast.error('Error al guardar la asignación de trabajadores');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  return {
    trabajadores,
    loading,
    saving,
    fetchTrabajadores,
    toggleTrabajador,
    guardarAsignacion,
  };
}
